import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { APP_NAME } from '../App';
import PermissionGate from '../components/common/PermissionGate';
import { downloadAsDocx } from '../utils/documentExporter';
import { downloadAsPdf } from '../utils/simplePdfGenerator';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  GitHub as GitHubIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PowerSettingsNew as PowerIcon,
  Architecture as ArchitectureIcon,
  AccountTree as FlowIcon,
  People as UserRolesIcon,
  Description as DocumentIcon,
  Api as ApiIcon,
  Security as SecurityIcon,
  Devices as TechStackIcon,
  Settings as DevelopmentIcon,
  Chat as ChatIcon,
  Palette as DesignIcon,
  ContentPaste as FieldsIcon,
  MiscellaneousServices as ServicesIcon,
  ViewTimeline as TimelineIcon,
  Article as Description,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import CustomAddIcon from '../components/layout/CustomAddIcon';
import CircularAddIcon from '../components/layout/CircularAddIcon';
import TaskSelectorDialog from '../components/projects/TaskSelectorDialog';
import ReactMarkdown from 'react-markdown';
import MainLayout from '../components/layout/MainLayout';
import AppDialog from '../components/layout/AppDialog';
import useProject from '../hooks/useProject';
import useDocuments from '../hooks/useDocuments';
import useNotification from '../hooks/useNotification';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';

// Import new components
import WorkflowDialog from '../components/prd-workflow/WorkflowDialog';
import ExtractPRD from '../components/prd-workflow/steps/ExtractPRD';
import ContentEditor from '../components/prd-workflow/ContentEditor';
import WorkflowChat from '../components/prd-workflow/WorkflowChat';
import Wireframes from '../components/prd-workflow/steps/Wireframes';
import Designs from '../components/prd-workflow/steps/Designs';
import Foundation from '../components/prd-workflow/Foundation';
import Build from '../components/prd-workflow/build';
import PRDGenerationButtons from '../components/prd-workflow/PRDGenerationButtons';

const PRDWorkflow = () => {
  const { projectId, documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Add useLocation to get navigation state

  // Function to get project initials for the avatar (copied from ProjectCard.js)
  function getProjectInitials(name) {
    if (!name) return '?';

    // Extract first letter of each word, up to 2 letters
    const initials = name
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');

    return initials || name.charAt(0).toUpperCase();
  }

  // Function to generate a consistent color based on the project name (copied from ProjectCard.js)
  function getAvatarColor(name) {
    if (!name) return '#6200EA'; // Default purple

    // List of colors to choose from
    const colors = [
      '#1E88E5', // Blue
      '#43A047', // Green
      '#E53935', // Red
      '#FB8C00', // Orange
      '#6D4C41', // Brown
      '#8E24AA', // Purple
      '#00ACC1', // Cyan
      '#3949AB', // Indigo
      '#039BE5', // Light Blue
      '#7CB342', // Light Green
      '#C0CA33', // Lime
      '#FDD835', // Yellow
      '#FFB300', // Amber
      '#F4511E', // Deep Orange
      '#5D4037', // Brown
      '#455A64', // Blue Grey
      '#EC407A', // Pink
      '#26A69A', // Teal
    ];

    // Generate a hash from the name to pick a consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use the hash to pick a color
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  // Use a ref to prevent re-fetching data multiple times
  const dataLoaded = useRef(false);

  // Disable projectId from being a dependency in hooks 
  const stableProjectId = useRef(projectId).current;

  const { project, refetchProject, updateProject, clearProjectCache, getProject } = useProject({ projectId: stableProjectId });
  const {
    loading,
    error,
    document: prdDocument,
    extractPRD,
    generateFieldValidations,
    generateTechStack,
    generateDevGuidelines,
    generateDesignGuidelines,
    generateSecurityGuidelines,
    generateAPIGuidelines,
    generateFeatures,
    generateUserStories,
    generateFinalPRD,
    connectToClaudeCode,
    setupGitRepository,
    deployApplication,
    trackPRDProgress,
    getDocumentById,
    updateDocument
  } = useDocuments();
  const { showNotification } = useNotification();

  // State
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localProject, setLocalProject] = useState(null);
  // Initialize activeStep from location state if available
  const [activeStep, setActiveStep] = useState(location.state?.activeStep || null);

  // Check if we should preserve tasks from location state
  const preserveTasks = location.state?.preserveTasks || false;
  const navigationTasks = location.state?.currentTasks || [];
  const [progress, setProgress] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [extractedPRD, setExtractedPRD] = useState(null);
  const [validations, setValidations] = useState(null);
  const [techStack, setTechStack] = useState(null);
  const [devGuidelines, setDevGuidelines] = useState(null);
  const [designGuidelines, setDesignGuidelines] = useState(null);
  const [wireframes, setWireframes] = useState(null);
  const [designs, setDesigns] = useState(null);
  const [securityGuidelines, setSecurityGuidelines] = useState(null);
  const [apiGuidelines, setApiGuidelines] = useState(null);
  const [features, setFeatures] = useState(null);
  const [userStories, setUserStories] = useState(null);
  const [finalPRD, setFinalPRD] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({});
  const [workflowStatus, setWorkflowStatus] = useState({});
  const [validationInstructions, setValidationInstructions] = useState('');
  const [designInfo, setDesignInfo] = useState({
    figmaUrl: '',
    designNotes: ''
  });
  const [gitConfig, setGitConfig] = useState({
    repoName: '',
    gitlabUrl: '',
    gitlabToken: ''
  });
  const [deployConfig, setDeployConfig] = useState({
    domain: '',
    platform: 'aws',
    apiKey: ''
  });

  // New state variables for additional features
  const [architecture, setArchitecture] = useState(null);
  const [processFlows, setProcessFlows] = useState(null);
  const [userRoles, setUserRoles] = useState(null);

  // Add Task Dialog
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);

  // Task Selector Dialog (from Foundation component)
  const [taskSelectorOpen, setTaskSelectorOpen] = useState(false);

  // Handler to open the task selector dialog
  const handleOpenTaskSelector = useCallback(async () => {
    // Ensure we have fresh project data before opening the task selector
    try {
      console.log('PRDWorkflow: Fetching fresh project data before opening task selector');

      // Use the explicit getProject function to ensure we get the latest data
      if (projectId) {
        const freshProject = await getProject(projectId);
        if (freshProject) {
          console.log('PRDWorkflow: Updated project before task selector:', freshProject);
          // Update local project state with fresh data
          setLocalProject(freshProject);
        }
      } else {
        // Fallback to refetch if no projectId available
        await refetchProject();
      }

      // Set a small timeout to ensure state updates before opening dialog
      setTimeout(() => {
        setTaskSelectorOpen(true);
      }, 100);
    } catch (error) {
      console.error('Error fetching project data before task selector:', error);
      showNotification({
        message: 'Failed to load project data. Please try again.',
        type: 'error'
      });
    }
  }, [projectId, getProject, refetchProject, showNotification]);

  // Helper function to save content before navigation
  const saveCurrentStepContent = useCallback(() => {
    // Check content based on current step
    switch (activeStep) {
      case 1: // Field Validations
        if (!validations && editContent && editContent.trim()) {
          setValidations({
            title: 'Field Validations',
            content: editContent,
            type: 'validation'
          });
          console.log("Auto-saved Field Validations content");
          showNotification({
            message: 'Field validations content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 2: // Technology Stack
        if (!techStack && editContent && editContent.trim()) {
          setTechStack({
            title: 'Technology Stack',
            content: editContent,
            type: 'techStack'
          });
          console.log("Auto-saved Technology Stack content");
          showNotification({
            message: 'Technology stack content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 3: // Development Guidelines
        if (!devGuidelines && editContent && editContent.trim()) {
          setDevGuidelines({
            title: 'Development Guidelines',
            content: editContent,
            type: 'devGuidelines'
          });
          console.log("Auto-saved Development Guidelines content");
          showNotification({
            message: 'Development guidelines content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 4: // Design Guidelines
        if (!designGuidelines && editContent && editContent.trim()) {
          setDesignGuidelines({
            title: 'Design Guidelines',
            content: editContent,
            type: 'designGuidelines'
          });
          console.log("Auto-saved Design Guidelines content");
          showNotification({
            message: 'Design guidelines content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 5: // Security Guidelines
        if (!securityGuidelines && editContent && editContent.trim()) {
          setSecurityGuidelines({
            title: 'Security Guidelines',
            content: editContent,
            type: 'securityGuidelines'
          });
          console.log("Auto-saved Security Guidelines content");
          showNotification({
            message: 'Security guidelines content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 8: // API Guidelines
        if (!apiGuidelines && editContent && editContent.trim()) {
          setApiGuidelines({
            title: 'API Guidelines',
            content: editContent,
            type: 'apiGuidelines'
          });
          console.log("Auto-saved API Guidelines content");
          showNotification({
            message: 'API guidelines content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 9: // Features
        if (!features && editContent && editContent.trim()) {
          setFeatures({
            title: 'Features',
            content: editContent,
            type: 'features'
          });
          console.log("Auto-saved Features content");
          showNotification({
            message: 'Features content saved',
            type: 'success'
          });
          return true;
        }
        break;
      case 10: // User Stories
        if (!userStories && editContent && editContent.trim()) {
          setUserStories({
            title: 'User Stories',
            content: editContent,
            type: 'userStories'
          });
          console.log("Auto-saved User Stories content");
          showNotification({
            message: 'User stories content saved',
            type: 'success'
          });
          return true;
        }
        break;
      default:
        break;
    }
    return false;
  }, [activeStep, editContent, validations, techStack, devGuidelines,
    designGuidelines, securityGuidelines, apiGuidelines, features, userStories, showNotification]);

  // Update workflow status - refactored to use useCallback
  const updateWorkflowStatus = useCallback(() => {
    try {
      // Check if we should update workflow status (prevents unnecessary updates)
      if (!project) {
        console.log('Skipping workflow status update - no project loaded');
        return;
      }

      console.log('Updating workflow status');
      const status = {
        extractPRD: { done: !!extractedPRD, status: extractedPRD ? 'completed' : 'pending' },
        fieldValidations: { done: !!validations, status: validations ? 'completed' : 'pending' },
        techStack: { done: !!techStack, status: techStack ? 'completed' : 'pending' },
        devGuidelines: { done: !!devGuidelines, status: devGuidelines ? 'completed' : 'pending' },
        designGuidelines: { done: !!designGuidelines, status: designGuidelines ? 'completed' : 'pending' },
        securityGuidelines: { done: !!securityGuidelines, status: securityGuidelines ? 'completed' : 'pending' },
        apiGuidelines: { done: !!apiGuidelines, status: apiGuidelines ? 'completed' : 'pending' },
        features: { done: !!features, status: features ? 'completed' : 'pending' },
        userStories: { done: !!userStories, status: userStories ? 'completed' : 'pending' },
        architecture: { done: !!architecture, status: architecture ? 'completed' : 'pending' },
        processFlows: { done: !!processFlows, status: processFlows ? 'completed' : 'pending' },
        userRoles: { done: !!userRoles, status: userRoles ? 'completed' : 'pending' },
        finalPRD: { done: !!finalPRD, status: finalPRD ? 'completed' : 'pending' },
        development: { done: false, status: 'pending' }
      };

      setWorkflowStatus(prev => {
        // Only update if status actually changed
        if (JSON.stringify(prev) !== JSON.stringify(status)) {
          return status;
        }
        return prev;
      });
    } catch (err) {
      console.error('Error updating workflow status:', err);
    }
  }, [project, extractedPRD, validations, techStack, devGuidelines, designGuidelines,
    securityGuidelines, apiGuidelines, features, userStories, architecture, processFlows, userRoles, finalPRD]);

  // One-time log when project is loaded
  useEffect(() => {
    if (localProject) {
      console.log("Project loaded successfully:", localProject.name);
    }
  }, []);

  // Load initial data - ONLY ONCE
  useEffect(() => {
    // If we've already loaded data, don't reload
    if (dataLoaded.current) {
      return;
    }

    let isMounted = true;
    let abortController = new AbortController();

    // We now use props instead of events for communication

    const loadInitialData = async () => {
      try {
        // Set the flag immediately to prevent concurrent loads
        dataLoaded.current = true;

        if (!isMounted) return;

        // Only proceed if we have a project ID
        if (!stableProjectId) {
          throw new Error('Project ID is required');
        }

        // Load the project data
        setLocalLoading(true);
        setLocalError(null);

        console.log('Loading project data ONCE');
        await refetchProject();

        if (!isMounted) return;

        if (!project) {
          throw new Error('Project not found');
        }

        // Single log with critical info
        console.log("Project loaded:", project?.name, "ID:", projectId);

        // Create a local copy of the project data with hardcoded values as backup
        const projectData = { ...project };

        // Ensure project has a tasks array
        if (!projectData.tasks) {
          projectData.tasks = [];
        }

        // If we're navigating from Foundation and should preserve tasks, use the tasks from navigation state
        if (preserveTasks && navigationTasks.length > 0) {
          console.log("Preserving tasks from navigation:", navigationTasks);
          projectData.tasks = navigationTasks;
        } else {
          // Hard-code default tasks when not preserving tasks from navigation
          projectData.tasks = ["dev-guidelines", "tech-stack", "field-validations", "wireframes", "designs", "build"];
        }

        // Set the local project
        setLocalProject(projectData);

        // Only load document if we have an ID and haven't loaded it yet
        if (documentId && !extractedPRD?._id) {
          console.log('Loading document:', documentId);
          const docData = await getDocumentById(documentId);
          if (!isMounted) return;

          if (docData) {
            setExtractedPRD(docData);
            // If we have a document, go to the validations step
            setActiveStep(1);
            console.log('Document loaded, setting activeStep to 1');
          } else {
            console.warn('Document not found:', documentId);
            // Go back to project workflow without a document
            navigate(`/projects/${projectId}/prd-workflow`);
            setActiveStep(null);
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error in loadInitialData:', err);
        setLocalError(err.message || 'Failed to load project data');
        setLocalProject(null);
      } finally {
        if (isMounted) {
          setLocalLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
      abortController.abort();
      // We now use props instead of events for communication
    };
    // Empty dependency array to ensure this only runs once on mount
  }, []);

  // Separate effect to update workflow status whenever relevant state changes
  // This effect only runs once on mount, since updateWorkflowStatus is memoized
  // and its dependencies are tracked in the useCallback
  useEffect(() => {
    console.log('Running workflow status update');
    updateWorkflowStatus();
    // Don't re-run this effect when updateWorkflowStatus changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation handlers
  const handleNext = () => {
    // Save any unsaved content
    saveCurrentStepContent();

    // Clear edit state when moving between steps
    setEditMode(false);
    setEditContent('');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);

    // Update workflow status after state changes
    setTimeout(() => {
      updateWorkflowStatus();
    }, 100);
  };

  const handleBack = () => {
    // Save any unsaved content
    saveCurrentStepContent();

    // Clear edit state when moving between steps
    setEditMode(false);
    setEditContent('');
    setActiveStep((prevActiveStep) => prevActiveStep - 1);

    // Update workflow status after state changes
    setTimeout(() => {
      updateWorkflowStatus();
    }, 100);
  };

  // Edit mode handlers
  const handleEditToggle = (content) => {
    setEditMode(!editMode);
    if (content !== false) {
      // Ensure content is a string
      const stringContent = typeof content === 'string' ? content : '';
      setEditContent(stringContent);
    } else {
      setEditContent('');
    }
  };

  const handleSaveEdit = async (content) => {
    try {
      const contentToSave = content || editContent;

      if (!contentToSave || typeof contentToSave !== 'string') {
        console.error('Invalid content type:', typeof contentToSave);
        showNotification({
          message: 'Cannot save: Invalid content type',
          type: 'error'
        });
        return;
      }

      // Save edited content based on current step
      switch (activeStep) {
        case 0: // Extract PRD
          setExtractedPRD(prev => ({ ...prev, content: contentToSave }));
          break;
        case 1: // Field Validations
          setValidations(prev => ({ ...prev, content: contentToSave }));
          break;
        case 2: // Technology Stack
          setTechStack(prev => ({ ...prev, content: contentToSave }));
          break;
        case 3: // Development Guidelines
          setDevGuidelines(prev => ({ ...prev, content: contentToSave }));
          break;
        case 4: // Design Guidelines
          setDesignGuidelines(prev => ({ ...prev, content: contentToSave }));
          break;
        case 5: // Security Guidelines
          setSecurityGuidelines(prev => ({ ...prev, content: contentToSave }));
          break;
        case 6: // API Guidelines
          setApiGuidelines(prev => ({ ...prev, content: contentToSave }));
          break;
        case 7: // Features
          setFeatures(prev => ({ ...prev, content: contentToSave }));
          break;
        case 8: // User Stories
          setUserStories(prev => ({ ...prev, content: contentToSave }));
          break;
        case 9: // Final PRD
          setFinalPRD(prev => ({ ...prev, content: contentToSave }));
          break;
        default:
          break;
      }

      setEditMode(false);
      showNotification({
        message: 'Changes saved successfully',
        type: 'success'
      });
    } catch (err) {
      console.error("Error saving edits:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to save changes"}`,
        type: 'error'
      });
    }
  };

  // Extract PRD handler
  const handleExtractPRD = async () => {
    try {
      setDialogOpen(false);
      setLocalLoading(true);

      // If we have a document ID, load that document directly
      if (documentId) {
        console.log('Loading existing PRD document:', documentId);
        const docData = await getDocumentById(documentId);
        if (docData) {
          setExtractedPRD(docData);
          showNotification({
            message: 'PRD loaded successfully from existing document',
            type: 'success'
          });
          handleNext();
          return;
        }
      }

      // Otherwise extract from conversations
      console.log('Extracting PRD from conversations');
      const extractedData = await extractPRD(projectId, {
        source: 'conversation',
        content: localProject?.conversations || []
      });

      if (!extractedData) {
        throw new Error('Failed to extract PRD content');
      }

      setExtractedPRD(extractedData);
      showNotification({
        message: 'PRD extracted successfully from conversations',
        type: 'success'
      });

      // Navigate to the document with the new document ID
      if (extractedData._id) {
        navigate(`/projects/${projectId}/prd-workflow/${extractedData._id}`);
      } else {
        handleNext();
      }
    } catch (err) {
      console.error("Error extracting/loading PRD:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to extract/load PRD"}`,
        type: 'error'
      });
    } finally {
      setLocalLoading(false);
    }
  };

  // Field validations handler
  const handleFieldValidations = async () => {
    try {
      setDialogOpen(false);

      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Call API to generate field validations
      const validationsData = await generateFieldValidations(
        extractedPRD._id,
        validationInstructions
      );

      setValidations(validationsData);
      showNotification({
        message: 'Field validations generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating field validations:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate field validations"}`,
        type: 'error'
      });
    }
  };

  // Technology stack handler
  const handleTechStack = async () => {
    try {
      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Call API to generate technology stack
      const techStackData = await generateTechStack(extractedPRD._id);

      setTechStack(techStackData);
      showNotification({
        message: 'Technology stack generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating technology stack:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate technology stack"}`,
        type: 'error'
      });
    }
  };

  // Development guidelines handler
  const handleDevGuidelines = async () => {
    try {
      if (!extractedPRD || !techStack) {
        throw new Error("PRD and technology stack are required");
      }

      // Call API to generate development guidelines
      const guidelinesData = await generateDevGuidelines(extractedPRD._id, techStack);

      setDevGuidelines(guidelinesData);
      showNotification({
        message: 'Development guidelines generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating development guidelines:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate development guidelines"}`,
        type: 'error'
      });
    }
  };

  // Design guidelines handler
  const handleDesignGuidelines = async () => {
    try {
      setDialogOpen(false);

      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Call API to generate design guidelines
      const designData = await generateDesignGuidelines(extractedPRD._id, designInfo);

      setDesignGuidelines(designData);
      showNotification({
        message: 'Design guidelines generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating design guidelines:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate design guidelines"}`,
        type: 'error'
      });
    }
  };

  // Security guidelines handler
  const handleSecurityGuidelines = async () => {
    try {
      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Call API to generate security guidelines
      const securityData = await generateSecurityGuidelines(extractedPRD._id);

      setSecurityGuidelines(securityData);
      showNotification({
        message: 'Security guidelines generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating security guidelines:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate security guidelines"}`,
        type: 'error'
      });
    }
  };

  // API guidelines handler
  const handleAPIGuidelines = async () => {
    try {
      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Call API to generate API guidelines
      const apiData = await generateAPIGuidelines(extractedPRD._id);

      setApiGuidelines(apiData);
      showNotification({
        message: 'API guidelines generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating API guidelines:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate API guidelines"}`,
        type: 'error'
      });
    }
  };

  // Features handler
  const handleFeatures = async () => {
    try {
      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Call API to generate features
      const featuresData = await generateFeatures(extractedPRD._id);

      setFeatures(featuresData);
      showNotification({
        message: 'Features generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating features:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate features"}`,
        type: 'error'
      });
    }
  };

  // User stories handler
  const handleUserStories = async () => {
    try {
      if (!extractedPRD || !features) {
        throw new Error("PRD and features are required");
      }

      // Call API to generate user stories
      const storiesData = await generateUserStories(extractedPRD._id, features);

      setUserStories(storiesData);
      showNotification({
        message: 'User stories generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating user stories:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate user stories"}`,
        type: 'error'
      });
    }
  };

  // Architecture handler
  const handleArchitecture = async () => {
    try {
      if (!extractedPRD || !techStack) {
        throw new Error("PRD and technology stack are required");
      }

      // Simulate API call for architecture generation
      // In a real implementation, this would call a backend API endpoint
      setLocalLoading(true);

      // This would be replaced with an actual API call
      const architectureData = {
        title: 'System Architecture',
        content: `# System Architecture for ${localProject?.name || 'Project'}

## Overview
This document outlines the architectural design for the ${localProject?.name || 'Project'} system, based on the defined technology stack and requirements.

## System Components
1. Frontend Layer
2. Backend Services
3. Database Layer
4. Integration Interfaces
5. Security Components

## Architecture Diagram
\`\`\`
+----------------+        +----------------+       +----------------+
|                |        |                |       |                |
|   Frontend     |<------>|   Backend API  |<----->|   Database     |
|                |        |                |       |                |
+----------------+        +----------------+       +----------------+
                                  ^
                                  |
                          +----------------+
                          |                |
                          |  External      |
                          |  Services      |
                          |                |
                          +----------------+
\`\`\`

## Design Patterns
- Repository Pattern for data access
- Factory Pattern for service creation
- Strategy Pattern for payment processing
- Observer Pattern for event handling

## Component Details
### Frontend
- React.js SPA with Redux state management
- Responsive design using Material UI
- Secure authentication flow

### Backend
- RESTful API with Node.js/Express
- Microservices architecture for core functions
- JWT authentication
- Rate limiting and request validation

### Database
- MongoDB for primary data storage
- Redis for caching and session management
- Data encryption at rest

### Integration Points
- Payment gateway integration
- Email service integration
- Third-party APIs

## Scalability Considerations
- Horizontal scaling of application servers
- Database sharding strategy
- CDN for static assets
- Containerization with Docker and Kubernetes orchestration

## Security Architecture
- End-to-end encryption for sensitive data
- OWASP security best practices implementation
- Regular security audits and penetration testing
`,
        type: 'architecture'
      };

      setTimeout(() => {
        setArchitecture(architectureData);
        setLocalLoading(false);

        showNotification({
          message: 'System architecture generated successfully',
          type: 'success'
        });
      }, 2000);

    } catch (err) {
      console.error("Error generating system architecture:", err);
      setLocalLoading(false);
      showNotification({
        message: `Error: ${err.message || "Failed to generate system architecture"}`,
        type: 'error'
      });
    }
  };

  // Process Flows handler
  const handleProcessFlows = async () => {
    try {
      if (!extractedPRD || !userStories) {
        throw new Error("PRD and user stories are required");
      }

      // Simulate API call for flow generation
      // In a real implementation, this would call a backend API endpoint
      setLocalLoading(true);

      // This would be replaced with an actual API call
      const flowsData = {
        title: 'Process Flows',
        content: `# Process Flows for ${localProject?.name || 'Project'}

## User Registration Flow
\`\`\`
Start --> User visits registration page
      --> User fills registration form
      --> System validates input
      --> System creates user account
      --> System sends verification email
      --> User clicks verification link
      --> Account activated
      --> End
\`\`\`

## Authentication Flow
\`\`\`
Start --> User enters credentials
      --> System validates credentials
      --> If valid --> Generate JWT token
                    --> Return token to client
                    --> Redirect to dashboard
      --> If invalid --> Show error message
                      --> Prompt to retry
      --> End
\`\`\`

## Purchase Workflow
\`\`\`
Start --> User browses products
      --> User adds items to cart
      --> User proceeds to checkout
      --> User enters shipping details
      --> User selects payment method
      --> System processes payment
      --> If successful --> Create order
                         --> Send confirmation
                         --> Update inventory
      --> If failed --> Show error
                     --> Prompt to retry
      --> End
\`\`\`

## Content Creation Flow
\`\`\`
Start --> User navigates to create content
      --> User adds title and content
      --> User uploads media (if applicable)
      --> System validates content
      --> User submits for publishing
      --> System moderates content
      --> If approved --> Publish content
                       --> Notify followers
      --> If rejected --> Send feedback to user
                       --> Allow edits
      --> End
\`\`\`

## Account Management Flow
\`\`\`
Start --> User accesses profile
      --> User selects edit profile
      --> User updates information
      --> System validates changes
      --> System updates profile
      --> User views updated profile
      --> End
\`\`\`

## Data Synchronization Flow
\`\`\`
Start --> System detects changes
      --> System initiates sync
      --> System resolves conflicts
      --> System updates records
      --> System logs sync activity
      --> End
\`\`\`

## Reporting Process
\`\`\`
Start --> User selects report type
      --> System fetches data
      --> System generates report
      --> User views/exports report
      --> End
\`\`\`
`,
        type: 'flows'
      };

      setTimeout(() => {
        setProcessFlows(flowsData);
        setLocalLoading(false);

        showNotification({
          message: 'Process flows generated successfully',
          type: 'success'
        });
      }, 2000);

    } catch (err) {
      console.error("Error generating process flows:", err);
      setLocalLoading(false);
      showNotification({
        message: `Error: ${err.message || "Failed to generate process flows"}`,
        type: 'error'
      });
    }
  };

  // User Roles handler
  const handleUserRoles = async () => {
    try {
      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Simulate API call for user roles generation
      // In a real implementation, this would call a backend API endpoint
      setLocalLoading(true);

      // This would be replaced with an actual API call
      const rolesData = {
        title: 'User Roles and Permissions',
        content: `# User Roles and Permissions for ${localProject?.name || 'Project'}

## Overview
This document defines the user roles and associated permissions within the system. Each role has specific capabilities and access rights designed to maintain proper security and operational functionality.

## User Roles

### 1. Administrator
**Description:** System administrators with full access to all features and settings.

**Permissions:**
- User management (create, read, update, delete all users)
- Content management (approve, edit, delete any content)
- System configuration and settings
- Access to analytics and reporting
- Security settings management
- Integration management
- Audit log access

### 2. Manager
**Description:** Department or team managers with elevated privileges.

**Permissions:**
- User management (create, read, update users in their department)
- Content management (approve, edit content in their department)
- Access to department analytics and reporting
- Workflow approval capabilities
- Limited system configuration access

### 3. Content Creator
**Description:** Users responsible for creating and managing content.

**Permissions:**
- Create and edit their own content
- Submit content for approval
- Basic analytics access for their content
- Media library access
- Template usage

### 4. Standard User
**Description:** Regular system users with basic privileges.

**Permissions:**
- View published content
- Update their own profile
- Submit feedback/comments
- Perform basic transactions
- Access to personal dashboard

### 5. Guest User
**Description:** Unauthenticated or limited access users.

**Permissions:**
- View public content only
- Register for an account
- Reset password
- Limited search functionality

## Role Hierarchy
\`\`\`
Administrator
    |
    +--- Manager
           |
           +--- Content Creator
                   |
                   +--- Standard User
                           |
                           +--- Guest User
\`\`\`

## Permission Matrix

| Permission | Administrator | Manager | Content Creator | Standard User | Guest User |
|------------|--------------|---------|-----------------|---------------|------------|
| User Management | Full | Department | None | Self only | None |
| Content Management | Full | Department | Own content | None | None |
| System Configuration | Full | Limited | None | None | None |
| Analytics Access | Full | Department | Own content | Limited | None |
| Approval Workflow | All levels | Department | Submit only | None | None |
| Financial Operations | Full | Limited | None | Self only | None |
| API Access | Full | Limited | Limited | None | None |

## Access Control Implementation
The system will implement role-based access control (RBAC) with additional attribute-based rules for specific scenarios. JWT tokens will contain role information, and server-side validation will enforce permissions on all API endpoints.
`,
        type: 'userRoles'
      };

      setTimeout(() => {
        setUserRoles(rolesData);
        setLocalLoading(false);

        showNotification({
          message: 'User roles and permissions generated successfully',
          type: 'success'
        });
      }, 2000);

    } catch (err) {
      console.error("Error generating user roles:", err);
      setLocalLoading(false);
      showNotification({
        message: `Error: ${err.message || "Failed to generate user roles"}`,
        type: 'error'
      });
    }
  };

  // Final PRD handler
  const handleFinalPRD = async () => {
    try {
      if (!extractedPRD) {
        throw new Error("PRD document is required");
      }

      // Collect all documents
      const allDocuments = {
        prd: extractedPRD,
        validations: validations,
        techStack: techStack,
        devGuidelines: devGuidelines,
        designGuidelines: designGuidelines,
        securityGuidelines: securityGuidelines,
        apiGuidelines: apiGuidelines,
        features: features,
        userStories: userStories
      };

      // Build the final PRD content with separate markdown files reference
      let finalPRDContent = `# ${localProject?.name || 'Project'} - Final PRD\n\n`;

      // Add table of contents for separate files
      finalPRDContent += `## Table of Contents\n\n`;
      finalPRDContent += `1. [Product Requirements Document](#prd)\n`;
      finalPRDContent += `2. [Field Validations](validation.md)\n`;
      finalPRDContent += `3. [Technology Stack](technology.md)\n`;
      finalPRDContent += `4. [Development Guidelines](development.md)\n`;
      finalPRDContent += `5. [Design Guidelines](design.md)\n`;
      finalPRDContent += `6. [Security Guidelines](security.md)\n`;
      finalPRDContent += `7. [API Guidelines](api.md)\n`;
      finalPRDContent += `8. [Features](features.md)\n`;
      finalPRDContent += `9. [User Stories](user-stories.md)\n\n`;

      // Add PRD content
      finalPRDContent += `## PRD\n\n`;
      finalPRDContent += extractedPRD.content;

      // Create final PRD object
      const finalPRDObject = {
        title: `${localProject?.name || 'Project'} - Final PRD`,
        content: finalPRDContent,
        type: 'finalPRD'
      };

      // Create array of all separate MD files
      const mdFiles = [
        {
          filename: 'validation.md',
          title: 'Field Validations',
          content: validations ? validations.content : '# Field Validations\n\n*No content provided*'
        },
        {
          filename: 'technology.md',
          title: 'Technology Stack',
          content: techStack ? techStack.content : '# Technology Stack\n\n*No content provided*'
        },
        {
          filename: 'development.md',
          title: 'Development Guidelines',
          content: devGuidelines ? devGuidelines.content : '# Development Guidelines\n\n*No content provided*'
        },
        {
          filename: 'design.md',
          title: 'Design Guidelines',
          content: designGuidelines ? designGuidelines.content : '# Design Guidelines\n\n*No content provided*'
        },
        {
          filename: 'security.md',
          title: 'Security Guidelines',
          content: securityGuidelines ? securityGuidelines.content : '# Security Guidelines\n\n*No content provided*'
        },
        {
          filename: 'api.md',
          title: 'API Guidelines',
          content: apiGuidelines ? apiGuidelines.content : '# API Guidelines\n\n*No content provided*'
        },
        {
          filename: 'features.md',
          title: 'Features',
          content: features ? features.content : '# Features\n\n*No content provided*'
        },
        {
          filename: 'user-stories.md',
          title: 'User Stories',
          content: userStories ? userStories.content : '# User Stories\n\n*No content provided*'
        }
      ];

      // Create the final PRD with all separate files
      const finalData = {
        ...finalPRDObject,
        mdFiles: mdFiles
      };

      // Call API to generate final PRD (still needed for backend storage)
      // This should be updated to include the mdFiles array if your backend supports it
      const apiResponse = await generateFinalPRD(projectId, {
        ...allDocuments,
        finalPRD: finalPRDObject,
        mdFiles: mdFiles
      });

      // If the API doesn't support the mdFiles format, use our local version
      setFinalPRD(apiResponse || finalData);

      showNotification({
        message: 'Final PRD with separate markdown files generated successfully',
        type: 'success'
      });

      // Move to next step
      handleNext();
    } catch (err) {
      console.error("Error generating final PRD:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to generate final PRD"}`,
        type: 'error'
      });
    }
  };

  // Claude Code connection handler
  const handleConnectClaudeCode = async () => {
    try {
      setDialogOpen(false);

      if (!finalPRD) {
        throw new Error("Final PRD is required");
      }

      // Collect all documents
      const allDocuments = {
        prd: extractedPRD,
        validations: validations,
        techStack: techStack,
        devGuidelines: devGuidelines,
        designGuidelines: designGuidelines,
        securityGuidelines: securityGuidelines,
        apiGuidelines: apiGuidelines,
        features: features,
        userStories: userStories,
        finalPRD: finalPRD
      };

      // Call API to connect to Claude Code
      const result = await connectToClaudeCode(projectId, allDocuments);

      showNotification({
        message: 'Successfully connected to Claude Code',
        type: 'success'
      });
    } catch (err) {
      console.error("Error connecting to Claude Code:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to connect to Claude Code"}`,
        type: 'error'
      });
    }
  };

  // Git repository setup handler
  const handleGitSetup = async () => {
    try {
      setDialogOpen(false);

      if (!finalPRD || !gitConfig.repoName) {
        throw new Error("Final PRD and repository name are required");
      }

      // Call API to setup Git repository
      const result = await setupGitRepository(projectId, gitConfig);

      showNotification({
        message: 'Git repository setup successful',
        type: 'success'
      });
    } catch (err) {
      console.error("Error setting up Git repository:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to setup Git repository"}`,
        type: 'error'
      });
    }
  };

  // Deployment handler
  const handleDeploy = async () => {
    try {
      setDialogOpen(false);

      if (!finalPRD || !deployConfig.domain) {
        throw new Error("Final PRD and domain are required");
      }

      // Call API to deploy application
      const result = await deployApplication(projectId, deployConfig);

      showNotification({
        message: 'Application deployment initiated',
        type: 'success'
      });
    } catch (err) {
      console.error("Error deploying application:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to deploy application"}`,
        type: 'error'
      });
    }
  };

  // Dialog handlers
  const openDialogForAction = (actionType) => {
    let dialogConfig = { type: actionType };

    switch (actionType) {
      case 'extractPRD':
        dialogConfig.title = documentId ? 'Load Existing PRD' : 'Extract PRD from Conversations';
        dialogConfig.content = documentId
          ? 'Loading existing PRD document...'
          : 'This will extract a new PRD from your project conversations. Continue?';
        break;
      case 'claudeCode':
        dialogConfig.title = 'Connect to Claude Code';
        break;
      case 'gitSetup':
        dialogConfig.title = 'Set Up Git Repository';
        break;
      case 'deploy':
        dialogConfig.title = 'Deploy Application';
        break;
      default:
        dialogConfig.title = 'PRD Workflow';
    }

    setDialogData(dialogConfig);
    setDialogOpen(true);
  };

  // Render document content
  const renderDocumentContent = (document) => {
    if (!document) return null;

    return (
      <Box>
        <ReactMarkdown>
          {document.content}
        </ReactMarkdown>
      </Box>
    );
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ExtractPRD
            extractedPRD={extractedPRD}
            editMode={editMode}
            editContent={editContent}
            onEditToggle={handleEditToggle}
            onSaveEdit={handleSaveEdit}
            onNext={handleNext}
            openDialog={openDialogForAction}
            documentId={documentId}
            loading={localLoading}
          />
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Field Validations
            </Typography>

            <ContentEditor
              title="Field Validations"
              content={validations?.content}
              onSave={(content) => {
                if (!validations) {
                  // Create a new validations object if it doesn't exist
                  setValidations({
                    title: 'Field Validations',
                    content: content,
                    type: 'validation'
                  });
                } else {
                  // Update existing validations
                  setValidations({
                    ...validations,
                    content: content
                  });
                }
              }}
              sectionName="Field Validations"
              projectId={projectId}
              generateLabel="Generate Field Validations"
              placeholder="Enter field validation rules as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Technology Stack
              </Button>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Technology Stack
            </Typography>

            <ContentEditor
              title="Technology Stack Recommendations"
              content={techStack?.content}
              onSave={(content) => {
                if (!techStack) {
                  // Create a new techStack object if it doesn't exist
                  setTechStack({
                    title: 'Technology Stack Recommendations',
                    content: content,
                    type: 'techStack'
                  });
                } else {
                  // Update existing techStack
                  setTechStack({
                    ...techStack,
                    content: content
                  });
                }
              }}
              sectionName="Technology Stack"
              projectId={projectId}
              generateLabel="Generate Technology Stack"
              placeholder="Enter technology stack recommendations as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Development Guidelines
              </Button>
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Development Guidelines
            </Typography>

            <ContentEditor
              title="Development Guidelines"
              content={devGuidelines?.content}
              onSave={(content) => {
                if (!devGuidelines) {
                  // Create a new devGuidelines object if it doesn't exist
                  setDevGuidelines({
                    title: 'Development Guidelines',
                    content: content,
                    type: 'devGuidelines'
                  });
                } else {
                  // Update existing devGuidelines
                  setDevGuidelines({
                    ...devGuidelines,
                    content: content
                  });
                }
              }}
              sectionName="Development Guidelines"
              projectId={projectId}
              generateLabel="Generate Development Guidelines"
              placeholder="Enter development guidelines, coding standards, and best practices as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Design Guidelines
              </Button>
            </Box>
          </Box>
        );
      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Design Guidelines
            </Typography>

            <ContentEditor
              title="Design Guidelines"
              content={designGuidelines?.content}
              onSave={(content) => {
                if (!designGuidelines) {
                  // Create a new designGuidelines object if it doesn't exist
                  setDesignGuidelines({
                    title: 'Design Guidelines',
                    content: content,
                    type: 'design_guidelines'
                  });
                } else {
                  // Update existing designGuidelines
                  setDesignGuidelines({
                    ...designGuidelines,
                    content: content
                  });
                }
              }}
              sectionName="Design Guidelines"
              projectId={projectId}
              generateLabel="Generate Design Guidelines"
              placeholder="Enter design guidelines as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Wireframes
              </Button>
            </Box>
          </Box>
        );
      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Wireframes
            </Typography>

            <Wireframes
              projectId={projectId}
              onSave={(content) => {
                if (!wireframes) {
                  // Create a new wireframes object if it doesn't exist
                  setWireframes({
                    title: 'Project Wireframes',
                    content: content,
                    type: 'wireframes'
                  });
                } else {
                  // Update existing wireframes
                  setWireframes({
                    ...wireframes,
                    content: content
                  });
                }
              }}
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Designs
              </Button>
            </Box>
          </Box>
        );
      case 6:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Designs
            </Typography>

            <Designs
              projectId={projectId}
              onSave={(content) => {
                if (!designs) {
                  // Create a new designs object if it doesn't exist
                  setDesigns({
                    title: 'Project Designs',
                    content: content,
                    type: 'designs'
                  });
                } else {
                  // Update existing designs
                  setDesigns({
                    ...designs,
                    content: content
                  });
                }
              }}
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Security Guidelines
              </Button>
            </Box>
          </Box>
        );
      case 7:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Security Guidelines
            </Typography>

            <ContentEditor
              title="Security Guidelines"
              content={securityGuidelines?.content}
              onSave={(content) => {
                if (!securityGuidelines) {
                  // Create a new securityGuidelines object if it doesn't exist
                  setSecurityGuidelines({
                    title: 'Security Guidelines',
                    content: content,
                    type: 'security_guidelines'
                  });
                } else {
                  // Update existing securityGuidelines
                  setSecurityGuidelines({
                    ...securityGuidelines,
                    content: content
                  });
                }
              }}
              sectionName="Security Guidelines"
              projectId={projectId}
              generateLabel="Generate Security Guidelines"
              placeholder="Enter security guidelines as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to API Guidelines
              </Button>
            </Box>
          </Box>
        );
      case 8:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              API Guidelines
            </Typography>

            <ContentEditor
              title="API Guidelines"
              content={apiGuidelines?.content}
              onSave={(content) => {
                if (!apiGuidelines) {
                  // Create a new apiGuidelines object if it doesn't exist
                  setApiGuidelines({
                    title: 'API Guidelines',
                    content: content,
                    type: 'api_guidelines'
                  });
                } else {
                  // Update existing apiGuidelines
                  setApiGuidelines({
                    ...apiGuidelines,
                    content: content
                  });
                }
              }}
              sectionName="API Guidelines"
              projectId={projectId}
              generateLabel="Generate API Guidelines"
              placeholder="Enter API design guidelines as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Features
              </Button>
            </Box>
          </Box>
        );
      case 9:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Features
            </Typography>

            <ContentEditor
              title="Features"
              content={features?.content}
              onSave={(content) => {
                if (!features) {
                  // Create a new features object if it doesn't exist
                  setFeatures({
                    title: 'Features',
                    content: content,
                    type: 'features'
                  });
                } else {
                  // Update existing features
                  setFeatures({
                    ...features,
                    content: content
                  });
                }
              }}
              sectionName="Features"
              projectId={projectId}
              generateLabel="Generate Features"
              placeholder="Enter features list as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to User Stories
              </Button>
            </Box>
          </Box>
        );
      case 10:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              User Stories
            </Typography>

            <ContentEditor
              title="User Stories"
              content={userStories?.content}
              onSave={(content) => {
                if (!userStories) {
                  // Create a new userStories object if it doesn't exist
                  setUserStories({
                    title: 'User Stories',
                    content: content,
                    type: 'user_stories'
                  });
                } else {
                  // Update existing userStories
                  setUserStories({
                    ...userStories,
                    content: content
                  });
                }
              }}
              sectionName="User Stories"
              projectId={projectId}
              generateLabel="Generate User Stories"
              placeholder="Enter user stories as markdown..."
            />

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to Final PRD
              </Button>
            </Box>
          </Box>
        );
      case 11:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Final PRD
            </Typography>

            {finalPRD ? (
              <Box mt={2}>
                <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {finalPRD.title || 'Final Product Requirements Document'}
                    <Chip
                      label="Final PRD Generated"
                      color="success"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Typography>

                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      Main PRD Document
                    </Typography>
                    <Box sx={{ maxHeight: '300px', overflow: 'auto', mt: 2, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <ReactMarkdown>
                        {finalPRD.content}
                      </ReactMarkdown>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>
                      Markdown Files
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      The following files have been generated as separate markdown documents for development references:
                    </Typography>

                    {finalPRD.mdFiles && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                        {finalPRD.mdFiles.map((file, index) => (
                          <Button
                            key={index}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              // Set edit content to this file's content for viewing
                              setEditContent(file.content);
                              // Open a dialog showing the file content
                              setDialogData({
                                type: 'viewFile',
                                title: file.title,
                                filename: file.filename,
                                content: file.content
                              });
                              setDialogOpen(true);
                            }}
                          >
                            {file.filename}
                          </Button>
                        ))}
                      </Box>
                    )}

                    <Box mt={3} display="flex" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => handleEditToggle(finalPRD.content)}
                        startIcon={<EditIcon />}
                      >
                        Edit Main PRD
                      </Button>
                    </Box>

                    {editMode && (
                      <Box mt={2}>
                        <CodeMirror
                          value={editContent}
                          height="400px"
                          onChange={(value) => setEditContent(value)}
                          extensions={[markdown()]}
                        />
                        <Box mt={2} display="flex" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            onClick={() => setEditMode(false)}
                            sx={{ mr: 1 }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveEdit}
                            startIcon={<SaveIcon />}
                          >
                            Save
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>

                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Continue to Development
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box mt={2}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Generate the final PRD document that combines all previous sections into separate markdown files. This will create individual .md files for validation, development, technology, design, API, security, user stories, and a main PRD file that integrates everything.
                </Alert>

                <Box sx={{ mt: 3, mb: 3 }}>
                  <ContentEditor
                    title="Final PRD Generation"
                    content={null}
                    onSave={(content) => {
                      // This will be used as instructions for the PRD generation
                      setEditContent(content);
                    }}
                    sectionName="Final PRD"
                    projectId={projectId}
                    generateLabel="Generate Final PRD with Files"
                    placeholder="Enter any additional instructions for the final PRD generation..."
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" mt={4}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleFinalPRD}
                    disabled={!extractedPRD}
                  >
                    Generate Final PRD with Markdown Files
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );
      case 12:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Development & Deployment
            </Typography>

            <Box mt={2}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Connect to Claude Code terminal to start development using the PRD. You can also set up a Git repository and deploy the application.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Connect to Claude Code
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Start development of the application using Claude Code terminal. This will use all the generated documents.
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => openDialogForAction('claudeCode')}
                          disabled={!finalPRD}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <PowerIcon fontSize="small" />
                          Connect to Claude Code
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Setup Git Repository
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Create a Git repository for the project code and commit the initial code.
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => openDialogForAction('gitSetup')}
                          disabled={!finalPRD}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <GitHubIcon fontSize="small" />
                          Setup Git Repository
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Deploy Application
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Deploy the application to a web server with configurable domain.
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => openDialogForAction('deploy')}
                          disabled={!finalPRD}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <CloudUploadIcon fontSize="small" />
                          Deploy Application
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`/projects/${projectId}`)}
                >
                  Back to Project
                </Button>
              </Box>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  // Render status icon based on workflow status
  const renderStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in-progress':
        return <InfoIcon color="primary" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return null;
    }
  };

  // Define new expanded workflow tiles including the additional ones
  const workflowTiles = [
    // New chat tile as first tile
    {
      id: 'chat-prd',
      title: 'Project Info',
      icon: <ChatIcon fontSize="large" />,
      description: 'Chat with your PRD to get insights and clarifications',
      actions: [
        { label: 'Chat', icon: <ChatIcon />, action: () => window.open(`/projects/${projectId}/conversation`, '_blank') },
        { label: 'View', icon: <VisibilityIcon />, action: () => { } }
      ],
      status: 'available',
      step: -1
    },
    // Original tiles with enhanced content
    {
      id: 'extract-prd',
      title: 'Extract PRD',
      icon: <DocumentIcon fontSize="large" />,
      description: 'Extract PRD content from conversations and documents',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(extractedPRD?.content) },
        { label: 'Chat', icon: <ChatIcon />, action: () => { } }
      ],
      status: !!extractedPRD ? 'completed' : 'pending',
      step: 0
    },
    {
      id: 'field-validations',
      title: 'Field Validations',
      icon: <FieldsIcon fontSize="large" />,
      description: 'Define field validation rules for all inputs',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(validations?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleFieldValidations() }
      ],
      status: !!validations ? 'completed' : 'pending',
      step: 1
    },
    {
      id: 'tech-stack',
      title: 'Technology Stack',
      icon: <TechStackIcon fontSize="large" />,
      description: 'Define the technology stack for the project',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(techStack?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleTechStack() }
      ],
      status: !!techStack ? 'completed' : 'pending',
      step: 2
    },
    {
      id: 'dev-guidelines',
      title: 'Development Guidelines',
      icon: <DevelopmentIcon fontSize="large" />,
      description: 'Set coding standards and development practices',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(devGuidelines?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleDevGuidelines() }
      ],
      status: !!devGuidelines ? 'completed' : 'pending',
      step: 3
    },
    {
      id: 'design-guidelines',
      title: 'Design Guidelines',
      icon: <DesignIcon fontSize="large" />,
      description: 'Define UI/UX design principles and standards',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(designGuidelines?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleDesignGuidelines() }
      ],
      status: !!designGuidelines ? 'completed' : 'pending',
      step: 4
    },
    {
      id: 'wireframes',
      title: 'Wireframes',
      icon: <DesignIcon fontSize="large" />,
      description: 'Create wireframes for your project UI',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => setActiveStep(5) },
        { label: 'View', icon: <VisibilityIcon />, action: () => setActiveStep(5) }
      ],
      status: !!wireframes ? 'completed' : 'pending',
      step: 5
    },
    {
      id: 'designs',
      title: 'Designs',
      icon: <DesignIcon fontSize="large" />,
      description: 'Create detailed designs based on wireframes',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => setActiveStep(6) },
        { label: 'View', icon: <VisibilityIcon />, action: () => setActiveStep(6) }
      ],
      status: !!designs ? 'completed' : 'pending',
      step: 6
    },
    {
      id: 'security-guidelines',
      title: 'Security Guidelines',
      icon: <SecurityIcon fontSize="large" />,
      description: 'Define security requirements and practices',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(securityGuidelines?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleSecurityGuidelines() }
      ],
      status: !!securityGuidelines ? 'completed' : 'pending',
      step: 7
    },
    {
      id: 'api-guidelines',
      title: 'API Guidelines',
      icon: <ApiIcon fontSize="large" />,
      description: 'Set standards for API design and implementation',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(apiGuidelines?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleAPIGuidelines() }
      ],
      status: !!apiGuidelines ? 'completed' : 'pending',
      step: 8
    },
    {
      id: 'features',
      title: 'Features',
      icon: <ServicesIcon fontSize="large" />,
      description: 'Define core features and functionality',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(features?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleFeatures() }
      ],
      status: !!features ? 'completed' : 'pending',
      step: 9
    },
    {
      id: 'user-stories',
      title: 'User Stories',
      icon: <Description fontSize="large" />,
      description: 'Create user stories for each feature',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(userStories?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleUserStories() }
      ],
      status: !!userStories ? 'completed' : 'pending',
      step: 10
    },
    // New additional tiles
    {
      id: 'architecture',
      title: 'Architecture',
      icon: <ArchitectureIcon fontSize="large" />,
      description: 'Generate system architecture diagrams and patterns',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(architecture?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleArchitecture() }
      ],
      status: !!architecture ? 'completed' : 'pending',
      step: 11
    },
    {
      id: 'flow',
      title: 'Process Flows',
      icon: <FlowIcon fontSize="large" />,
      description: 'Generate sequence diagrams and process flows',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(processFlows?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleProcessFlows() }
      ],
      status: !!processFlows ? 'completed' : 'pending',
      step: 11
    },
    {
      id: 'user-roles',
      title: 'User Roles',
      icon: <UserRolesIcon fontSize="large" />,
      description: 'Define user roles and permissions',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(userRoles?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleUserRoles() }
      ],
      status: !!userRoles ? 'completed' : 'pending',
      step: 11
    },
    {
      id: 'final-prd',
      title: 'Final PRD',
      icon: <DocumentIcon fontSize="large" />,
      description: 'Compile all sections into the final PRD document',
      actions: [
        { label: 'Edit', icon: <EditIcon />, action: () => handleEditToggle(finalPRD?.content) },
        { label: 'Generate', icon: <RefreshIcon />, action: () => handleFinalPRD() }
      ],
      status: !!finalPRD ? 'completed' : 'pending',
      step: 11
    },
    {
      id: 'development',
      title: 'Development',
      icon: <CodeIcon fontSize="large" />,
      description: 'Deploy and develop the application',
      actions: [
        { label: 'Connect', icon: <PowerIcon />, action: () => openDialogForAction('claudeCode') },
        { label: 'Deploy', icon: <CloudUploadIcon />, action: () => openDialogForAction('deploy') }
      ],
      status: 'pending',
      step: 12
    },
    {
      id: 'build',
      title: 'Build',
      icon: <TechStackIcon fontSize="large" />,
      description: 'Plan and track the development process',
      actions: [
        { label: 'View', icon: <VisibilityIcon />, action: () => window.location.href = `/projects/${projectId}/prd-workflow#build` },
        { label: 'Edit', icon: <EditIcon />, action: () => window.location.href = `/projects/${projectId}/prd-workflow#build` }
      ],
      status: 'available',
      step: -1,
      onClick: () => window.location.href = `/projects/${projectId}/prd-workflow#build`
    }
  ];

  // Force set to null on mount for dashboard view
  useEffect(() => {
    console.log('Current activeStep:', activeStep);
    // Initial load - set to null to show dashboard unless we have a document
    if (documentId && activeStep === 0) {
      console.log('Document ID exists, keeping activeStep at 0');
    } else if (!documentId && activeStep === 0) {
      console.log('Setting activeStep to null to show dashboard');
      setActiveStep(null);
    }
  }, [documentId, activeStep]);

  // Function to download document as PDF
  const handleDownloadPDF = (tile) => {
    try {
      showNotification({
        message: `Downloading ${tile.title} as PDF...`,
        type: 'info'
      });

      // Get the content based on the tile
      let content = '';
      let documentTitle = tile.title;

      // Determine which content to download based on tile id
      switch (tile.id) {
        case 'extracted-prd':
          content = extractedPRD?.content || '';
          documentTitle = extractedPRD?.title || tile.title;
          break;
        case 'field-validations':
          content = validations?.content || '';
          documentTitle = validations?.title || tile.title;
          break;
        case 'tech-stack':
          content = techStack?.content || '';
          documentTitle = techStack?.title || tile.title;
          break;
        case 'dev-guidelines':
          content = devGuidelines?.content || '';
          documentTitle = devGuidelines?.title || tile.title;
          break;
        case 'design-guidelines':
          content = designGuidelines?.content || '';
          documentTitle = designGuidelines?.title || tile.title;
          break;
        case 'security-guidelines':
          content = securityGuidelines?.content || '';
          documentTitle = securityGuidelines?.title || tile.title;
          break;
        case 'api-guidelines':
          content = apiGuidelines?.content || '';
          documentTitle = apiGuidelines?.title || tile.title;
          break;
        case 'features':
          content = features?.content || '';
          documentTitle = features?.title || tile.title;
          break;
        case 'user-stories':
          content = userStories?.content || '';
          documentTitle = userStories?.title || tile.title;
          break;
        case 'final-prd':
          content = finalPRD?.content || '';
          documentTitle = finalPRD?.title || tile.title;
          break;
        default:
          throw new Error('Content not available for this document');
      }

      if (!content) {
        throw new Error('No content available to download');
      }

      // Download as PDF
      downloadAsPdf(content, documentTitle);

      showNotification({
        message: `${tile.title} PDF downloaded successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showNotification({
        message: error.message || 'Failed to download PDF',
        type: 'error'
      });
    }
  };

  // Function to download document as DOCX
  const handleDownloadDOCX = (tile) => {
    try {
      showNotification({
        message: `Downloading ${tile.title} as DOCX...`,
        type: 'info'
      });

      // Get the content based on the tile
      let content = '';
      let documentTitle = tile.title;

      // Determine which content to download based on tile id
      switch (tile.id) {
        case 'extracted-prd':
          content = extractedPRD?.content || '';
          documentTitle = extractedPRD?.title || tile.title;
          break;
        case 'field-validations':
          content = validations?.content || '';
          documentTitle = validations?.title || tile.title;
          break;
        case 'tech-stack':
          content = techStack?.content || '';
          documentTitle = techStack?.title || tile.title;
          break;
        case 'dev-guidelines':
          content = devGuidelines?.content || '';
          documentTitle = devGuidelines?.title || tile.title;
          break;
        case 'design-guidelines':
          content = designGuidelines?.content || '';
          documentTitle = designGuidelines?.title || tile.title;
          break;
        case 'security-guidelines':
          content = securityGuidelines?.content || '';
          documentTitle = securityGuidelines?.title || tile.title;
          break;
        case 'api-guidelines':
          content = apiGuidelines?.content || '';
          documentTitle = apiGuidelines?.title || tile.title;
          break;
        case 'features':
          content = features?.content || '';
          documentTitle = features?.title || tile.title;
          break;
        case 'user-stories':
          content = userStories?.content || '';
          documentTitle = userStories?.title || tile.title;
          break;
        case 'final-prd':
          content = finalPRD?.content || '';
          documentTitle = finalPRD?.title || tile.title;
          break;
        default:
          throw new Error('Content not available for this document');
      }

      if (!content) {
        throw new Error('No content available to download');
      }

      // Download as DOCX
      downloadAsDocx(content, documentTitle);

      showNotification({
        message: `${tile.title} DOCX downloaded successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      showNotification({
        message: error.message || 'Failed to download DOCX',
        type: 'error'
      });
    }
  };

  // Main render
  // Define a function to handle opening the add task dialog
  const handleOpenAddTaskDialog = () => {
    // Initialize empty selected tasks - only show tasks not already activated
    setSelectedTasks([]);

    // Show a message if there are no additional tasks to activate
    const availableTasks = workflowTiles.filter(tile =>
      tile.id !== 'chat-prd' &&
      tile.id !== 'extract-prd' &&
      !(localProject?.tasks && localProject.tasks.includes(tile.id))
    );

    console.log("Available tasks to activate:", availableTasks.map(t => t.id));

    // Open the dialog
    setAddTaskDialogOpen(true);
  };

  // Function to handle task selection/deselection
  const handleTaskSelection = (taskId) => {
    setSelectedTasks(prevSelectedTasks => {
      if (prevSelectedTasks.includes(taskId)) {
        // If task is already selected, remove it
        return prevSelectedTasks.filter(id => id !== taskId);
      } else {
        // If task is not selected, add it
        return [...prevSelectedTasks, taskId];
      }
    });
  };

  // Function to reload project data
  const reloadProjectData = async () => {
    try {
      setLocalLoading(true);
      // Clear the cache first to ensure we get fresh data
      clearProjectCache(projectId);

      // Fetch fresh project data
      await refetchProject();

      console.log("Reloaded project data:", project);

      // Ensure project has a tasks array
      if (project && !project.tasks) {
        const updatedProject = { ...project, tasks: [] };
        await updateProject(projectId, updatedProject);
      }

      return project;
    } catch (err) {
      console.error("Error reloading project data:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to reload project data"}`,
        type: 'error'
      });
    } finally {
      setLocalLoading(false);
    }
  };

  // Function to save selected tasks to the project
  const handleSaveTasks = async () => {
    try {
      setLocalLoading(true);

      // Only proceed if there are newly selected tasks
      if (projectId && selectedTasks.length > 0) {
        console.log("Adding new tasks:", selectedTasks);

        // Combine existing tasks with newly selected ones
        const existingTasks = localProject?.tasks || [];
        const updatedTasks = [...new Set([...existingTasks, ...selectedTasks])]; // Use Set to remove duplicates

        console.log("Existing tasks:", existingTasks);
        console.log("Combined tasks after update:", updatedTasks);

        // Call the API to update the project tasks
        // Fix: pass projectId and projectData as an object with named parameters
        const updatedProject = await updateProject({
          projectId: projectId,
          projectData: { tasks: updatedTasks }
        });

        console.log("Updated project from API:", updatedProject);

        // Make sure we have the tasks array in the updated project
        if (!updatedProject.tasks) {
          updatedProject.tasks = [...updatedTasks];
        }

        // Update the local project state with the response from the API
        setLocalProject(updatedProject);

        // Reload the project data to ensure we have the latest
        await reloadProjectData();

        // Dispatch custom event to notify Foundation component to refresh its data
        window.dispatchEvent(new Event('taskSelectionCompleted'));

        showNotification({
          message: `${selectedTasks.length} ${selectedTasks.length === 1 ? 'topic' : 'topics'} activated successfully`,
          type: 'success'
        });
      }

      setAddTaskDialogOpen(false);
    } catch (err) {
      console.error("Error saving tasks:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to save tasks"}`,
        type: 'error'
      });
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <Box className="main-page-container" sx={{ width: '100%', bgcolor: '#f5f6f7', paddingLeft: '24px', paddingRight: '24px' }}>
      <Box sx={{ marginTop: 3 }}>
        <Box className="page-title-container" sx={{px:4}}>
          <Box sx={{ display: 'flex', alignItems: 'center', }}>
            {/* Project Avatar Removed */}

            {/* Project name and section only */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>

              {/* Project name + Section */}
              <Typography variant="h5" component="h1" fontWeight={600} sx={{display:'flex'}}>
                {project?.name || `${APP_NAME} System`}
                <Box component="span" sx={{ fontWeight: 400, ml: 1, color: 'text.secondary' }}>
                  - {window.location.hash === '#build' ? 'Build' :
                    window.location.hash === '#deploy' ? 'Deploy' :
                      window.location.hash === '#maintenance' ? 'Maintenance' : 'Foundation'}
                </Box>
              </Typography>
            </Box>
          </Box>
          {window.location.hash === '#foundation' && projectId && (
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      ml: 2
                    }}>
                      <PRDGenerationButtons projectId={projectId} />
                    </Box>
                  )}
                  {/* Add topics */}
          <PermissionGate permission="foundation.task.add">
            <IconButton
              color="primary"
              onClick={handleOpenTaskSelector}
              sx={{
                color: '#475969',
                '&:hover': {
                  bgcolor: 'transparent',
                }
              }}
              title='Add topics'
            >
              <CircularAddIcon sx={{ width: 30, height: 30 }} fill="#475969" />
            </IconButton>
          </PermissionGate>
        </Box>

      </Box>
      {activeStep === null || activeStep === undefined ? (
        // Dashboard View with Tiles
        <Box sx={{ pt: 3, pb: 6, px: 4, bgcolor: 'white' }}>
          {/* Only show Foundation component when specifically requested by the user clicking on a Foundation link */}
          {window.location.hash === '#foundation' && (
            <Foundation
              projectId={projectId}
              project={localProject}
              showNotification={showNotification}
              onOpenTaskSelector={handleOpenTaskSelector}
            />
          )}

          {/* Show Build component when specifically requested by the user clicking on a Build link */}
          {window.location.hash === '#build' && (
            <Build
              projectId={projectId}
              project={localProject}
              showNotification={showNotification}
              onOpenTaskSelector={handleOpenTaskSelector}
            />
          )}

          {/* Original tiles - only show when not in Foundation or Build view */}
          {window.location.hash !== '#foundation' && window.location.hash !== '#build' && (
            <Grid container spacing={4} sx={{ mt: 1 }} alignItems="flex-start">
              {workflowTiles
                // Filter tiles based on selected tasks in project or show default ones if project.tasks is undefined
                .filter(tile => {
                  // Always show the chat tile and extract PRD tile
                  if (tile.id === 'chat-prd' || tile.id === 'extract-prd') {
                    return true;
                  }

                  // Check if project has tasks and if this tile is included
                  if (localProject?.tasks && Array.isArray(localProject.tasks)) {
                    console.log("Checking if", tile.id, "is in", localProject.tasks);
                    return localProject.tasks.includes(tile.id);
                  }

                  return false;
                })
                .map((tile) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={tile.id}>
                    <Box
                      sx={{
                        height: 160,
                        width: '100%',
                        maxWidth: 260,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        backgroundColor: '#f5f6f7',
                        border: '1px solid',
                        borderColor: 'rgba(0, 0, 0, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        padding: 3,
                        '&:hover': {
                          backgroundColor: '#f0f0f0',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      {/* Completed checkmark */}
                      {tile.status === 'completed' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            bgcolor: '#4CAF50',
                            color: 'white',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 14 }} />
                        </Box>
                      )}

                      {/* Menu button */}
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
                        }}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();

                          // Create a menu component rather than direct DOM manipulation
                          const menu = document.createElement('div');
                          menu.id = `menu-${tile.id}`;
                          menu.style.position = 'absolute';
                          menu.style.top = `${e.clientY}px`;
                          menu.style.left = `${e.clientX}px`;
                          menu.style.backgroundColor = 'white';
                          menu.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
                          menu.style.borderRadius = '4px';
                          menu.style.zIndex = '1000';
                          menu.style.padding = '8px 0';
                          menu.style.minWidth = '160px';

                          // PDF option with proper styling and icon
                          const pdfOption = document.createElement('div');
                          pdfOption.innerHTML = '<span style="display: flex; align-items: center;"><svg style="width: 18px; height: 18px; margin-right: 8px;" viewBox="0 0 24 24"><path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9.5 8.5h3V15H13v-2h-2.5v-1.5h3V10h-4v6h3v-1.5h-3V11.5zM8 10h3v1.5H8V10zm0 3h1.5v1.5H8V13z"/></svg>Download PDF</span>';
                          pdfOption.style.padding = '8px 16px';
                          pdfOption.style.cursor = 'pointer';
                          pdfOption.style.transition = 'background-color 0.2s';
                          pdfOption.addEventListener('mouseover', () => {
                            pdfOption.style.backgroundColor = '#f5f5f5';
                          });
                          pdfOption.addEventListener('mouseout', () => {
                            pdfOption.style.backgroundColor = 'transparent';
                          });
                          pdfOption.onclick = () => {
                            handleDownloadPDF(tile);
                            document.body.removeChild(menu);
                          };

                          // DOCX option with proper styling and icon
                          const docxOption = document.createElement('div');
                          docxOption.innerHTML = '<span style="display: flex; align-items: center;"><svg style="width: 18px; height: 18px; margin-right: 8px;" viewBox="0 0 24 24"><path fill="currentColor" d="M21 5v14h-18v-14h18m0-2h-18c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2v-14c0-1.1-.9-2-2-2z"/><path fill="currentColor" d="M14 15h1.5v1.5h-1.5z"/><path fill="currentColor" d="M14 12h1.5v1.5h-1.5z"/><path fill="currentColor" d="M10 15h2.5v1.5h-2.5z"/><path fill="currentColor" d="M7.5 15h1v1.5h-1z"/><path fill="currentColor" d="M11 12h1.5v1.5h-1.5z"/><path fill="currentColor" d="M8 12h1.5v1.5h-1.5z"/></svg>Download DOCX</span>';
                          docxOption.style.padding = '8px 16px';
                          docxOption.style.cursor = 'pointer';
                          docxOption.style.transition = 'background-color 0.2s';
                          docxOption.addEventListener('mouseover', () => {
                            docxOption.style.backgroundColor = '#f5f5f5';
                          });
                          docxOption.addEventListener('mouseout', () => {
                            docxOption.style.backgroundColor = 'transparent';
                          });
                          docxOption.onclick = () => {
                            handleDownloadDOCX(tile);
                            document.body.removeChild(menu);
                          };

                          // Edit option with proper styling and icon
                          const editOption = document.createElement('div');
                          editOption.innerHTML = '<span style="display: flex; align-items: center;"><svg style="width: 18px; height: 18px; margin-right: 8px;" viewBox="0 0 24 24"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>Edit</span>';
                          editOption.style.padding = '8px 16px';
                          editOption.style.cursor = 'pointer';
                          editOption.style.transition = 'background-color 0.2s';
                          editOption.addEventListener('mouseover', () => {
                            editOption.style.backgroundColor = '#f5f5f5';
                          });
                          editOption.addEventListener('mouseout', () => {
                            editOption.style.backgroundColor = 'transparent';
                          });
                          editOption.onclick = () => {
                            const conversationUrl = `/projects/${projectId}/conversation?task=${tile.id}`;
                            window.open(conversationUrl, '_blank');
                            document.body.removeChild(menu);
                          };

                          // Add divider
                          const divider = document.createElement('div');
                          divider.style.height = '1px';
                          divider.style.backgroundColor = '#e0e0e0';
                          divider.style.margin = '4px 0';

                          menu.appendChild(editOption);
                          menu.appendChild(divider);
                          menu.appendChild(pdfOption);
                          menu.appendChild(docxOption);
                          document.body.appendChild(menu);

                          // Remove menu on click outside
                          setTimeout(() => {
                            const closeMenu = () => {
                              if (document.body.contains(menu)) {
                                document.body.removeChild(menu);
                              }
                              document.removeEventListener('click', closeMenu);
                            };
                            document.addEventListener('click', closeMenu);
                          }, 100);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                          width: '100%',
                          gap: 3
                        }}
                        onClick={() => {
                          // If the tile has a custom onClick handler, use it
                          if (tile.onClick) {
                            tile.onClick();
                            return;
                          }

                          // For Project Info tile, navigate to conversation
                          if (tile.id === 'chat-prd') {
                            const conversationUrl = `/projects/${projectId}/conversation`;
                            window.open(conversationUrl, '_blank');
                          } else {
                            // For other tiles, open the content in a new window/tab
                            const newWindowUrl = `/projects/${projectId}/prd-workflow/edit?step=${tile.step}&type=${tile.id}`;
                            window.open(newWindowUrl, '_blank');
                          }
                        }}
                      >
                        {tile.icon && React.isValidElement(tile.icon)
                          ? React.cloneElement(tile.icon, { style: { fontSize: '2.25rem', color: '#4a4a4a' } })
                          : <span style={{ fontSize: '2.25rem' }}>📄</span>
                        }

                        <Typography
                          variant="body1"
                          component="h2"
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 400,
                            color: '#4a4a4a',
                            textAlign: 'center',
                            px: 2,
                            width: '100%'
                          }}
                        >
                          {tile.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          )}
        </Box>
      ) : (
        // Content View for Selected Step
        <Box sx={{ pt: 3, pb: 6, px: 3 }}>
          <Paper
            sx={{
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 0,
              position: 'relative',
              minHeight: '600px',
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.05)'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                zIndex: 10
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  // Save content
                  saveCurrentStepContent();

                  // Reset active step to show the dashboard view
                  setActiveStep(null);
                }}
                startIcon={<ArrowForwardIcon style={{ transform: 'rotate(180deg)' }} />}
              >
                Back to Dashboard
              </Button>
            </Box>

            <Box mt={5}>
              {renderStepContent(activeStep)}
            </Box>
          </Paper>
        </Box>
      )}

      <WorkflowDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        dialogData={dialogData}
        gitConfig={gitConfig}
        deployConfig={deployConfig}
        onGitConfigChange={setGitConfig}
        onDeployConfigChange={setDeployConfig}
        onDialogDataChange={setDialogData}
        onSubmit={() => {
          switch (dialogData.type) {
            case 'extractPRD':
              handleExtractPRD();
              break;
            case 'claudeCode':
              handleConnectClaudeCode();
              break;
            case 'gitSetup':
              handleGitSetup();
              break;
            case 'deploy':
              handleDeploy();
              break;
            default:
              setDialogOpen(false);
          }
        }}
      />

      {/* Task Selector Dialog - Same as in Foundation component */}
      <TaskSelectorDialog
        open={taskSelectorOpen}
        onClose={() => setTaskSelectorOpen(false)}
        projectId={projectId} // Pass project ID to enable orchestrator editing
        selectedTasks={[]} // Start with an empty array, since we only want to select new tasks
        onSave={async (selectedTasks) => {
          try {
            if (!projectId) {
              throw new Error('Project ID is required');
            }

            // Check if any tasks were selected
            if (!selectedTasks || selectedTasks.length === 0) {
              showNotification({
                message: 'Please select at least one topic to add',
                type: 'warning'
              });
              return;
            }

            // Get the most up-to-date project data before combining tasks
            let currentProject = localProject;
            try {
              // Try to get fresh project data to ensure we have the latest tasks
              if (projectId) {
                const freshProject = await getProject(projectId);
                if (freshProject) {
                  console.log('Fresh project data before saving tasks:', freshProject);
                  currentProject = freshProject;
                }
              }
            } catch (error) {
              console.warn('Could not refresh project data before saving tasks:', error);
              // Continue with existing data
            }

            // Combine existing tasks with newly selected tasks
            const existingTasks = currentProject?.tasks || [];
            console.log('Existing tasks before combining:', existingTasks);
            console.log('Newly selected tasks:', selectedTasks);

            const combinedTasks = [...new Set([...existingTasks, ...selectedTasks])];
            console.log('Combined tasks:', combinedTasks);

            // Update project with combined tasks
            const updatedProject = await updateProject({
              projectId,
              projectData: { tasks: combinedTasks }
            });

            // Update the local state with the response from the API
            if (updatedProject) {
              console.log('Project updated successfully:', updatedProject);
              setLocalProject(updatedProject);
            } else {
              console.warn('No updated project returned from API, updating local state');
              // Fallback if API doesn't return the updated project
              setLocalProject(prev => ({
                ...prev,
                tasks: combinedTasks // Use combined tasks instead of just the newly selected tasks
              }));
            }

            showNotification({
              message: `${selectedTasks.length} ${selectedTasks.length === 1 ? 'task' : 'tasks'} activated successfully!`,
              type: 'success'
            });

            // Close the dialog
            setTaskSelectorOpen(false);

            // Reload project data to ensure we have the latest
            await reloadProjectData();

            // Dispatch custom event to notify Foundation component to refresh its data
            window.dispatchEvent(new Event('taskSelectionCompleted'));
          } catch (error) {
            console.error('Error saving tasks:', error);
            showNotification({
              message: `Failed to save tasks: ${error?.message || error?.response?.data?.message || 'Unexpected error'}`,
              type: 'error'
            });
          }
        }}
        title="Select extra topics to activate for your project"
        existingProjectTasks={localProject?.tasks || []} // Pass existing tasks to filter out
      />

      {/* Add Task Dialog */}
      <AppDialog
        open={addTaskDialogOpen}
        onClose={() => !localLoading && setAddTaskDialogOpen(false)}
        title="Select extra topics you want to activate for your project"
        maxWidth="lg"
        actions={
          <Button
            onClick={handleSaveTasks}
            variant="contained"
            disabled={selectedTasks.length === 0 || localLoading}
            startIcon={localLoading && <CircularProgress size={14} color="inherit" />}
            sx={{
              bgcolor: '#c94897',
              '&:hover': {
                bgcolor: '#b13d83',
              },
              fontWeight: 500,
              borderRadius: '8px',
              textTransform: 'none',
              py: 1,
              px: 3,
              fontSize: '0.9rem',
              height: '36px'
            }}
          >
            {localLoading ? 'Saving...' : 'Save'}
          </Button>
        }
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
          }
        }}
        DialogTitleProps={{
          sx: {
            borderBottom: '1px solid #e0e0e0',
            mb: 1,
            px: 5, // Add horizontal padding for the title
            py: 2.5, // Increased vertical padding
            '& .MuiTypography-root': {
              fontSize: '1.1rem', // Reduce the font size of the title
              fontWeight: 500,
            }
          }
        }}
        CloseButtonProps={{
          sx: {
            bgcolor: '#ccc',
            color: 'white',
            width: '28px',
            height: '28px',
            '& .MuiSvgIcon-root': {
              fontSize: '1rem', // Smaller X icon
            },
            '&:hover': {
              bgcolor: '#aaa',
            }
          }
        }}
      >
        {localLoading && (
          <Box sx={{ width: '100%', mb: 3 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Show message if all tasks are already selected */}
        {workflowTiles.filter(tile =>
          tile.id !== 'chat-prd' &&
          tile.id !== 'extract-prd' &&
          !(localProject?.tasks && localProject.tasks.includes(tile.id))
        ).length === 0 && (
            <Box sx={{
              textAlign: 'center',
              py: 5,
              color: 'text.secondary',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              mb: 3
            }}>
              <Typography variant="body1" gutterBottom>
                All available topics are already activated.
              </Typography>
              <Typography variant="body2">
                There are no additional topics available to add.
              </Typography>
            </Box>
          )}

        <Box sx={{ px: 5, py: 3 }}> {/* Increased padding all around */}
          <Grid container spacing={4} sx={{ mt: 1 }} alignItems="flex-start">
            {workflowTiles
              .filter(tile => {
                // Don't show chat-prd in the selection dialog
                if (tile.id === 'chat-prd') {
                  return false;
                }

                // Don't show extract-prd or tiles that are already selected/activated
                // Only show remaining tasks that aren't already saved
                if (tile.id === 'extract-prd' || (localProject?.tasks && localProject.tasks.includes(tile.id))) {
                  return false;
                }

                // Show all other tiles that haven't been activated yet
                return true;
              })
              .map((tile) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={tile.id}>
                  <Box
                    onClick={() => handleTaskSelection(tile.id)}
                    sx={{
                      height: 160,
                      width: '100%',
                      maxWidth: 260,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundColor: selectedTasks.includes(tile.id) ? '#fef4cf' : '#f5f6f7',
                      border: '1px solid',
                      borderColor: selectedTasks.includes(tile.id) ? '#fbc02d' : 'rgba(0, 0, 0, 0.05)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      padding: 3,
                      '&:hover': {
                        backgroundColor: selectedTasks.includes(tile.id) ? '#fef4cf' : '#f0f0f0',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                      },
                      opacity: localLoading ? 0.7 : 1
                    }}
                  >
                    {selectedTasks.includes(tile.id) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          zIndex: 2,
                          bgcolor: '#4CAF50',
                          color: 'white',
                          width: 20, // Reduced size from 24 to 20
                          height: 20, // Reduced size from 24 to 20
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14 }} /> {/* Reduced size from 16 to 14 */}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 3 // Keep consistent spacing
                      }}
                    >
                      {React.cloneElement(tile.icon, {
                        style: {
                          fontSize: '2.25rem', // Slightly smaller icons
                          color: '#4a4a4a' // Same color for selected or not
                        }
                      })}

                      <Typography
                        variant="body1"
                        component="h2"
                        sx={{
                          fontSize: '0.9rem', // Smaller font size
                          fontWeight: 400, // Normal weight instead of bold
                          color: '#4a4a4a', // Same color for selected or not
                          textAlign: 'center'
                        }}
                      >
                        {tile.title}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))
            }
          </Grid>
        </Box>
      </AppDialog>
    </Box>
  );
};

export default PRDWorkflow;